import { component, field, system, System, World } from '@lastolivegames/becsy';
import readline from "readline";
import { Readable } from 'stream';
import { $decoder, $encoder, $track } from './types/symbols';
import { encodeSchemaOperation } from './encoder/EncodeOperation';
import { decodeSchemaOperation } from './decoder/DecodeOperation';
import { ChangeTree } from './encoder/ChangeTree';
import { OPERATION } from './encoding/spec';
import { Metadata } from './Metadata';

@component class Position {
    @field.float64 declare x: number;
    @field.float64 declare y: number;
}

@component class Velocity {
    @field.float64 declare vx: number;
    @field.float64 declare vy: number;
}

@system export class VelocityInputController extends System {
    // Every system can define any number of queries whose results will be available in the `execute`
    // method.  In this case, we're asking for all entities that currently have a Velocity component,
    // and declare that we'll be writing to those components.
    private readonly movables = this.query(q => q.current.with(Velocity).write);

    // Here we'll store all keys that are currently pressed.  This is not specific to ECS but it's a
    // common pattern to glue together event-driven (DOM) and timing-driven (ECS) processes.
    private readonly keysPressed = new Set<string>();

    // Every system can provide an `initialize` method that will be called once as the world is being
    // set up.  We'll use it to register our DOM event handlers.
    initialize(): void {
        // Listen for keypress events
        const timeouts: {[key: string]: NodeJS.Timeout} = {};

        process.stdin.on('keypress', (str, key) => {
            if (key.ctrl && key.name === 'c') {
                // If Ctrl+C is pressed, exit the program
                rl.close();

            } else {
                // Print the key pressed
                this.keysPressed.add(key.name);

                if (timeouts[key.name]) {
                    clearTimeout(timeouts[key.name]);
                }

                timeouts[key.name] = setTimeout(() => {
                    this.keysPressed.delete(key.name);
                    delete timeouts[key.name];
                }, 100);
            }
        });
    }

    // Every system can (and probably should) provide an `execute` method that implements its logic.
    // It will be invoked once per frame in our demo, so at 60fps it's called 60 times per second.
    execute(): void {
        // We loop through the query results of the movables query we defined above.
        for (const movable of this.movables.current) {
            // This is how we access the data stored in the Velocity component of our movable entity.
            // We must specify whether we intend to only `read` the data or also to `write` it.  We'll
            // only be allowed to `write` to component types that we reserved as such in our queries.
            const velocity = movable.write(Velocity);

            if (this.keysPressed.has('up')) velocity.vy = -100;  // in pixels per second
            else if (this.keysPressed.has('down')) velocity.vy = 100;
            else velocity.vy = 0;

            if (this.keysPressed.has('left')) velocity.vx = -100;
            else if (this.keysPressed.has('right')) velocity.vx = 100;
            else velocity.vx = 0;
        }
    }
}

@system export class Movement extends System {
    private readonly movables =
        this.query(q => q.current.with(Velocity).and.with(Position).write);

    execute(): void {
        for (const movable of this.movables.current) {
            // We retrive both velocity (to read) and position (to write) from our entities.
            const velocity = movable.read(Velocity);
            const position = movable.write(Position);
            // In the execute method, a system has access to `this.delta`, which is the delta time between
            // the current frame and the previous one.  This allows us to calculate a stable movement
            // regardless of the intervals between our frames.  For more on that see
            // https://drewcampbell92.medium.com/understanding-delta-time-b53bf4781a03.
            position.x += this.delta * velocity.vx;
            position.y += this.delta * velocity.vy;
            console.log("x:", position.x, "y:", position.y);
        }
    }
}

Metadata.setFields(World, {
    x: "number",
    y: "number",
    z: "number",
});

World[$encoder] = encodeSchemaOperation;
World[$decoder] = decodeSchemaOperation;
World[$track] = function (
    changeTree: ChangeTree,
    index: number,
    operation: OPERATION = OPERATION.ADD
) {
    changeTree.change(index, operation);
};

async function main() {
    // We can now create the world that all our entities and their components will live in.  All system
    // and component classes tagged with `@system` and `@component` will be automatically added to the
    // world's `defs`, and in this case we don't need to add any other types manually.
    const world = await World.create();

    // Now we create the entity that will represent our object and add the components it will need.
    // Each component type can be optionally followed by an object with initial field values.
    world.createEntity(Position, Velocity);

    // Finally, we set up our game loop.  The `run` function will be executed once per frame.
    async function run() {
        // Execute the world, which will call the `execute` method of all systems in sequence.  The call
        // is asynchronous and we _must_ await its result, otherwise errors won't be reported properly.
        await world.execute();
    }

    setInterval(() => run(), 1000 / 30);
}

main();

// Create an interface for reading input
const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
});

// Start listening for input
process.stdin.setRawMode(true);
process.stdin.resume();